import React, { memo } from 'react';
export type MathVisualKind = 'gallery-fractions' | 'gallery-percentages' | 'gallery-equations' | 'gallery-geometry' | 'gallery-powers' | 'gallery-probability' | 'volume-fractions' | 'volume-rule-three' | 'volume-equations' | 'volume-geometry' | 'volume-roots' | 'volume-cartesian' | 'bonus-tables' | 'bonus-checklist' | 'bonus-calendar' | 'bonus-problems';
type MathVisualProps = {
  kind: MathVisualKind;
};
type VisualDefinition = {
  eyebrow: string;
  title: string;
  formula: string;
  note: string;
  tiles: string[];
  accent: 'yellow' | 'blue' | 'green' | 'red';
};
const visualDefinitions: Record<MathVisualKind, VisualDefinition> = {
  'gallery-fractions': {
    eyebrow: 'MAPA VISUAL',
    title: 'FRACCIONES',
    formula: '1/2 + 1/4 = 3/4',
    note: 'Dibujá · compará · resolvé',
    tiles: ['1/2', '1/4', '3/4'],
    accent: 'yellow'
  },
  'gallery-percentages': {
    eyebrow: 'EJEMPLO RESUELTO',
    title: 'PORCENTAJES',
    formula: '25% de 80 = 20',
    note: 'Convertí el porcentaje en una parte',
    tiles: ['10%', '25%', '50%'],
    accent: 'blue'
  },
  'gallery-equations': {
    eyebrow: 'PASO A PASO',
    title: 'ECUACIONES',
    formula: '2x + 5 = 15  →  x = 5',
    note: 'Lo que suma, pasa restando',
    tiles: ['2x', '− 5', '= 10'],
    accent: 'green'
  },
  'gallery-geometry': {
    eyebrow: 'FÓRMULAS CLAVE',
    title: 'GEOMETRÍA',
    formula: 'Área = base × altura',
    note: 'Medí, reemplazá y calculá',
    tiles: ['△', '□', '○'],
    accent: 'red'
  },
  'gallery-powers': {
    eyebrow: 'REGLAS CLAVE',
    title: 'POTENCIAS',
    formula: '2³ = 2 × 2 × 2 = 8',
    note: 'La potencia repite multiplicaciones',
    tiles: ['2²', '3²', '4²'],
    accent: 'yellow'
  },
  'gallery-probability': {
    eyebrow: 'EJEMPLO COTIDIANO',
    title: 'PROBABILIDAD',
    formula: 'P(par) = 3/6 = 1/2',
    note: 'Casos favorables / casos posibles',
    tiles: ['⚀', '⚁', '1/2'],
    accent: 'blue'
  },
  'volume-fractions': {
    eyebrow: 'TOMO 1',
    title: 'PIZZA DE FRACCIONES',
    formula: '2/8 = 1/4',
    note: 'Fracciones equivalentes con dibujos',
    tiles: ['1/2', '2/4', '4/8'],
    accent: 'yellow'
  },
  'volume-rule-three': {
    eyebrow: 'TOMO 2',
    title: 'REGLA DE TRES',
    formula: '3 → 12 · 5 → x',
    note: 'Multiplicá en cruz y dividí',
    tiles: ['3', '12', '20'],
    accent: 'blue'
  },
  'volume-equations': {
    eyebrow: 'TOMO 3',
    title: 'BALANZA ALGEBRAICA',
    formula: 'x + 7 = 14',
    note: 'Mantené ambos lados equilibrados',
    tiles: ['x', '+7', '14'],
    accent: 'green'
  },
  'volume-geometry': {
    eyebrow: 'TOMO 4',
    title: 'ÁREA Y PERÍMETRO',
    formula: 'P = 2(b + h)',
    note: 'Cada figura tiene su estrategia',
    tiles: ['b', 'h', 'cm²'],
    accent: 'red'
  },
  'volume-roots': {
    eyebrow: 'TOMO 5',
    title: 'POTENCIAS Y RAÍCES',
    formula: '√49 = 7',
    note: 'La raíz deshace la potencia',
    tiles: ['²', '√', '7'],
    accent: 'yellow'
  },
  'volume-cartesian': {
    eyebrow: 'TOMO 6',
    title: 'PLANO CARTESIANO',
    formula: 'A = (2, 3)',
    note: 'Primero x, después y',
    tiles: ['x', 'y', '(2,3)'],
    accent: 'blue'
  },
  'bonus-tables': {
    eyebrow: 'BONO IMPRIMIBLE',
    title: 'TABLAS',
    formula: '7 × 8 = 56',
    note: 'Repaso rápido todos los días',
    tiles: ['6×6', '7×8', '9×9'],
    accent: 'yellow'
  },
  'bonus-checklist': {
    eyebrow: 'BONO 5 MINUTOS',
    title: 'CHECKLIST',
    formula: '✓ datos  ✓ cuenta  ✓ respuesta',
    note: 'Antes de entregar, revisá solo/a',
    tiles: ['✓', '✓', '✓'],
    accent: 'green'
  },
  'bonus-calendar': {
    eyebrow: 'BONO ORGANIZADOR',
    title: 'CALENDARIO',
    formula: 'LUN · MIÉ · VIE',
    note: 'Un tema a la vez, sin apuro',
    tiles: ['L', 'M', 'V'],
    accent: 'red'
  },
  'bonus-problems': {
    eyebrow: 'BONO PRÁCTICA',
    title: 'PROBLEMAS',
    formula: '12 ÷ 3 + 4 = 8',
    note: 'Entendé el proceso, no memorices',
    tiles: ['①', '②', '③'],
    accent: 'blue'
  }
};
const accentClasses: Record<VisualDefinition['accent'], string> = {
  yellow: 'bg-[#FFC72C] text-[#1B3A6B]',
  blue: 'bg-sky-100 text-[#1B3A6B]',
  green: 'bg-emerald-100 text-emerald-900',
  red: 'bg-rose-100 text-rose-900'
};
export function MathVisual({
  kind
}: MathVisualProps) {
  const visual = visualDefinitions[kind];
  return <div aria-label={`${visual.title}: ${visual.formula}`} className="relative h-44 overflow-hidden rounded-xl border border-[#1B3A6B]/15 bg-[#FFFEFA] p-4 text-left text-[#1B3A6B]" data-id="element-19">
      <div className="absolute right-3 top-3 h-14 w-14 border-l border-t border-[#1B3A6B]/15" data-id="element-20" />
      <div className="absolute bottom-3 right-3 grid grid-cols-3 gap-1 opacity-30" data-id="element-21">
        {Array.from({
        length: 9
      }).map((_, index) => <span className="h-2.5 w-2.5 border border-[#1B3A6B]" key={index} data-id="element-22" />)}
      </div>
      <p className="relative text-[10px] font-black tracking-[0.16em] text-[#1B3A6B]/60" data-id="element-23">
        {visual.eyebrow}
      </p>
      <div className="relative mt-1 flex items-start justify-between gap-3" data-id="element-24">
        <h4 className="text-lg font-black tracking-tight" data-id="element-25">{visual.title}</h4>
        <span className={`rounded-md px-2 py-1 text-xs font-black ${accentClasses[visual.accent]}`} data-id="element-26">
          {visual.tiles[0]}
        </span>
      </div>
      <div className="relative mt-4 inline-block rounded-lg border-2 border-[#1B3A6B] bg-white px-3 py-2 font-mono text-sm font-bold shadow-sm" data-id="element-27">
        {visual.formula}
      </div>
      <div className="relative mt-3 flex items-center gap-2" data-id="element-28">
        {visual.tiles.slice(1).map(tile => <span className={`rounded-md px-2 py-1 text-xs font-extrabold ${accentClasses[visual.accent]}`} key={tile} data-id="element-29">
            {tile}
          </span>)}
        <p className="ml-auto max-w-[130px] text-right text-[10px] font-bold leading-3 text-[#4A5568]" data-id="element-30">
          {visual.note}
        </p>
      </div>
    </div>;
}